export type TeamRole = 'owner' | 'admin' | 'agent';

export type ChatVisibility = 'all' | 'assigned' | 'department';

export type MemberPermissions = {
  automation: boolean;
  aiAgent: boolean;
  contacts: boolean;
  templates: boolean;
  campaigns: boolean;
  voiceCalls: boolean;
  orders: boolean;
  showContactInfo: boolean;
  settings: boolean;
  clearChat: boolean;
  chatVisibility: ChatVisibility;
};

export const ROLE_PRESETS: Record<TeamRole, MemberPermissions> = {
  owner: {
    automation: true,
    aiAgent: true,
    contacts: true,
    templates: true,
    campaigns: true,
    voiceCalls: true,
    orders: true,
    showContactInfo: true,
    settings: true,
    clearChat: true,
    chatVisibility: 'all',
  },
  admin: {
    automation: true,
    aiAgent: true,
    contacts: true,
    templates: false,
    campaigns: true,
    voiceCalls: true,
    orders: true,
    showContactInfo: true,
    settings: false,
    clearChat: true,
    chatVisibility: 'assigned',
  },
  agent: {
    automation: false,
    aiAgent: false,
    contacts: false,
    templates: false,
    campaigns: false,
    voiceCalls: false,
    orders: true,
    showContactInfo: false,
    settings: false,
    clearChat: false,
    chatVisibility: 'assigned',
  },
};

export function getPermissions(role: string, customPermissions?: MemberPermissions | null): MemberPermissions {
  if (role === 'owner') return ROLE_PRESETS.owner;
  const preset = ROLE_PRESETS[role as TeamRole] || ROLE_PRESETS.agent;
  if (customPermissions) {
    let visibility = customPermissions.chatVisibility || preset.chatVisibility || 'assigned';
    // STRICT RULE: An agent must NEVER have 'all' chat visibility
    if (role === 'agent' && visibility === 'all') {
      visibility = 'assigned';
    }
    return {
      ...preset,
      ...customPermissions,
      templates: role === 'owner', // Templates strictly accessible only to owner
      orders: true, // Always allowed for all team roles (agents see own contacts' orders, owner sees all)
      showContactInfo: typeof (customPermissions as any).showContactInfo === 'boolean'
        ? (customPermissions as any).showContactInfo
        : (preset.showContactInfo ?? false),
      clearChat: typeof (customPermissions as any).clearChat === 'boolean'
        ? (customPermissions as any).clearChat
        : (preset.clearChat ?? false),
      chatVisibility: visibility,
    };
  }
  return {
    ...preset,
    templates: role === 'owner',
    orders: true,
  };
}

export function hasPermission(
  role: string,
  permissions: MemberPermissions | null | undefined,
  resource: keyof Omit<MemberPermissions, 'chatVisibility'>
): boolean {
  if (role === 'owner') return true;
  if (resource === 'templates') return false; // Strictly owner only
  if (resource === 'orders') return true; // Orders always allowed for all team members
  const perms = getPermissions(role, permissions);
  return perms[resource] === true;
}

export function canSeeAllChats(role: string, permissions?: MemberPermissions | null | undefined): boolean {
  if (role === 'owner') return true;
  // STRICT RULE: Agents must NEVER see all chats in any case
  if (role === 'agent') return false;
  const perms = getPermissions(role, permissions);
  return perms.chatVisibility === 'all';
}

export function getChatVisibility(role: string, permissions?: MemberPermissions | null | undefined): ChatVisibility {
  if (role === 'owner') return 'all';
  const perms = getPermissions(role, permissions);
  if (role === 'agent' && perms.chatVisibility === 'all') return 'assigned';
  return perms.chatVisibility || 'assigned';
}

export type PermissionResource = keyof Omit<MemberPermissions, 'chatVisibility'>;

export const ROUTE_PERMISSIONS: Record<string, PermissionResource> = {
  '/automation': 'automation',
  '/settings/ai': 'aiAgent',
  '/contacts': 'contacts',
  '/templates': 'templates',
  '/templates/new': 'templates',
  '/campaigns': 'campaigns',
  '/settings/voice': 'voiceCalls',
  '/calls': 'voiceCalls',
  '/orders': 'orders',
  '/settings': 'settings',
};

export function getRequiredPermissionForPath(pathname: string): { resource: PermissionResource; label: string } | null {
  const cleanPath = pathname.replace(/^\/[a-z]{2}(?=\/)/, '');

  // Sales Pipeline and Orders are always granted to all team members (owner, admin, agent)
  if (cleanPath === '/pipeline' || cleanPath.startsWith('/pipeline/')) {
    return null;
  }
  if (cleanPath === '/orders' || cleanPath.startsWith('/orders/')) {
    return null;
  }

  if (cleanPath === '/automation' || cleanPath.startsWith('/automation/')) {
    return { resource: 'automation', label: 'Automation' };
  }
  if (cleanPath === '/settings/ai' || cleanPath.startsWith('/settings/ai/')) {
    return { resource: 'aiAgent', label: 'AI Agent' };
  }
  if (cleanPath === '/contacts' || cleanPath.startsWith('/contacts/')) {
    return { resource: 'contacts', label: 'Contacts' };
  }
  if (cleanPath === '/templates' || cleanPath.startsWith('/templates/')) {
    return { resource: 'templates', label: 'Templates' };
  }
  if (cleanPath === '/campaigns' || cleanPath.startsWith('/campaigns/')) {
    return { resource: 'campaigns', label: 'Campaigns' };
  }
  if (cleanPath === '/calls' || cleanPath.startsWith('/calls/') || cleanPath === '/settings/voice' || cleanPath.startsWith('/settings/voice/')) {
    return { resource: 'voiceCalls', label: 'Voice Calls' };
  }
  if (cleanPath === '/settings' || cleanPath.startsWith('/settings/')) {
    // Password change under /settings/security is allowed for own profile
    if (cleanPath === '/settings/security') {
      return null;
    }
    return { resource: 'settings', label: 'Settings' };
  }

  return null;
}

